import { message, Button } from 'antd';
import { Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColorBlockSpecsFragment } from '../../../../api/fragments/ColorBlockSpecs';
import { type MatrixSpecsFragment } from '../../../../api/fragments/MatrixSpecs';
import { type TrimBlockSpecsFragment } from '../../../../api/fragments/TrimBlockSpecs';
import Form from '../../../../components/fields/Form';
import FormAutoTouch from '../../../../components/FormAutoTouch';
import useDebounce from '../../../../utilities/useDebounce';
import useHandleError from '../../../../utilities/useHandleError';
import { type VariantConfiguratorDetailsPageProps } from '../utils';
import { useVariantConfigurator } from '../VariantConfiguratorContext';
import VariantConfiguratorLayout from '../VariantConfiguratorLayout';
import ColorBlockForm from './ColorBlockForm';
import useColorBlockValidator from './useColorBlockValidator';
import useHandleBlockSave from './useHandleBlockSave';
import { type ColorBlockFormValues, getDefaultColorBlock, FORM_NAME } from './utils';

const ColorBlockTab = ({ activeTab, replaceTab, disabled = false }: VariantConfiguratorDetailsPageProps) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);
    const { variantConfigurator, refetch } = useVariantConfigurator();

    const [colorBlock, trimBlock, matrix] = useMemo(
        () => [variantConfigurator.colorBlock, variantConfigurator.trimBlock, variantConfigurator.matrix],
        [variantConfigurator]
    );

    const handleBlockSave = useHandleBlockSave();

    const getInitialValues = useCallback(
        (
            block: ColorBlockSpecsFragment,
            trimBlock?: TrimBlockSpecsFragment,
            matrix?: MatrixSpecsFragment[]
        ): ColorBlockFormValues => {
            if (!block) {
                return getDefaultColorBlock(trimBlock);
            }

            const colorTrimCombo = matrix?.reduce((accumulator, currentValue) => {
                const previousColorAccumulator = { ...accumulator[currentValue.colorId] };

                return {
                    ...accumulator,
                    [currentValue.colorId]: {
                        ...previousColorAccumulator,
                        [currentValue.trimId]: currentValue.variantColorImage,
                    },
                };
            }, {});

            const colorSettings = block.colorSettings.map(color => {
                const sectionImages = trimBlock?.trimSettings.map(trim => ({
                    trimId: trim.id,
                    trimName: trim.name,
                    variantColorImage: color?.id ? colorTrimCombo[color.id]?.[trim.id] : null,
                }));

                return {
                    hex: color.hex,
                    name: color.name,
                    code: color.code,
                    id: color.id,
                    price: color.price,
                    image: color.image,
                    sectionImages,
                };
            });

            return {
                type: block.type,
                colorBlockInput: {
                    id: block.id,
                    isActive: block.isActive,
                    sectionTitle: block.sectionTitle,
                    colorSettings,
                },
            };
        },
        []
    );

    const onSubmit = useDebounce(
        useHandleError<ColorBlockFormValues>(
            async (values, helpers) => {
                message.loading({
                    content: t('variantConfiguratorDetails:messages.color.saveSubmitting'),
                    key: 'primary',
                    duration: 0,
                });

                await handleBlockSave(values, variantConfigurator.id, colorBlock);

                message.success({
                    content: t('variantConfiguratorDetails:messages.color.saveSuccessful'),
                    key: 'primary',
                });

                const updatedVariantConfigurator = await refetch();
                helpers.resetForm({
                    values: getInitialValues(
                        updatedVariantConfigurator?.colorBlock,
                        updatedVariantConfigurator?.trimBlock,
                        updatedVariantConfigurator?.matrix
                    ),
                });
            },
            [colorBlock, getInitialValues, handleBlockSave, refetch, t, variantConfigurator.id]
        )
    );

    const validate = useColorBlockValidator();

    return (
        <VariantConfiguratorLayout
            activeTab={activeTab}
            footer={[
                !disabled && (
                    <Button key="submit" form={FORM_NAME} htmlType="submit" type="primary">
                        {t('variantConfiguratorDetails:actions.colors.save')}
                    </Button>
                ),
            ]}
            replaceTab={replaceTab}
        >
            <Formik<ColorBlockFormValues>
                initialValues={getInitialValues(colorBlock, trimBlock, matrix)}
                onSubmit={onSubmit}
                validate={validate}
            >
                {({ handleSubmit }) => (
                    <Form id={FORM_NAME} name={FORM_NAME} onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <ColorBlockForm disabled={disabled} trimBlock={trimBlock} />
                    </Form>
                )}
            </Formik>
        </VariantConfiguratorLayout>
    );
};

export default ColorBlockTab;
